import { useState } from 'react';
import { useClientIntegrations, useClientWebhookDeliveries, useClientWebhooks, useCreateClientApiKey, useDisableClientWebhook, useRevokeClientApiKey, useRotateClientApiKey, useSetClientApi, useStudioProjects } from '@/api/studio';
import type { Client } from '@/api/types';
import { ApiKeys } from '@/components/ApiKeys';
import { WebhookEndpoints, WebhookLog } from '@/components/Webhooks';
import { useLogPaging } from '@/lib/webhooks';
import { QueryPending } from '@/components/QueryState';
import { formatWhen } from '@/lib/format';

/**
 * «Интеграции» of the client card (MK42-6384): the API switch, the client's keys (never a key itself)
 * and the recent calls. Keys of a real client are its owner's business; the studio issues them only to a test client.
 */
export function ClientIntegrations({ client, canSwitch }: { client: Client; canSwitch: boolean }) {
  const [offset, setOffset] = useState(0);
  const q = useClientIntegrations(client.id, offset);
  const { data: projects } = useStudioProjects(client.id);
  const toggle = useSetClientApi();
  const create = useCreateClientApiKey();
  const rotate = useRotateClientApiKey();
  const revoke = useRevokeClientApiKey();
  const data = q.data;
  const cid = client.id;
  return (
    <section data-testid="client-integrations">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Интеграции</h2>
        {data && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={data.apiEnabled} disabled={!canSwitch || toggle.isPending} onChange={(e) => toggle.mutate({ clientId: cid, on: e.target.checked })} />
            API включено
          </label>
        )}
      </div>
      {!data ? (
        <QueryPending query={q} compact />
      ) : (
        <>
          <p className="mb-3 text-sm text-muted">
            {data.apiEnabled ? 'Владелец кабинета клиента выпускает ключи в «Компания и команда → Интеграции».' : 'Выключено: раздела у клиента нет, выпущенные ключи не работают.'}
            {data.isTest && ' Тестовый клиент: ключи выпускает студия.'}
          </p>
          {toggle.error && (
            <p role="alert" className="mb-2 text-sm text-overdue">
              {(toggle.error as Error).message}
            </p>
          )}
          <ApiKeys
            keys={data.keys}
            projects={projects ?? []}
            empty="Ключей нет."
            actions={
              data.mayIssue && data.apiEnabled
                ? {
                    issue: (input) => create.mutateAsync({ clientId: cid, input }),
                    rotate: (keyId) => rotate.mutateAsync({ clientId: cid, keyId }),
                    revoke: (keyId) => revoke.mutateAsync({ clientId: cid, keyId }),
                    pending: create.isPending || rotate.isPending || revoke.isPending,
                  }
                : undefined
            }
          />
          <h3 className="mb-2 mt-6 text-sm font-medium">Последние вызовы</h3>
          {data.calls.items.length === 0 ? (
            <p className="text-sm text-muted">Вызовов не было.</p>
          ) : (
            <>
              <table className="w-full text-left text-xs">
                <thead className="text-muted">
                  <tr>
                    <th className="py-1 font-normal">Когда</th>
                    <th className="py-1 font-normal">Ключ</th>
                    <th className="py-1 font-normal">Запрос</th>
                    <th className="py-1 text-right font-normal">Ответ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line border-y border-line">
                  {data.calls.items.map((r) => (
                    <tr key={r.id}>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{formatWhen(r.at)}</td>
                      <td className="py-1.5 pr-2">{r.keyName ?? (r.keyId ? r.keyId : 'неизвестный ключ')}</td>
                      <td className="py-1.5 pr-2 font-mono">
                        {r.method} {r.route}
                      </td>
                      <td className={r.status >= 400 ? 'num py-1.5 text-right text-overdue' : 'num py-1.5 text-right'}>
                        {r.status} · {r.ms} мс
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 flex items-center gap-2 text-xs text-muted">
                <span>
                  {data.calls.offset + 1}–{data.calls.offset + data.calls.items.length} из {data.calls.total}
                </span>
                <button className="btn-ghost h-7 px-2 text-xs" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - data.calls.limit))}>
                  Новее
                </button>
                <button className="btn-ghost h-7 px-2 text-xs" disabled={data.calls.offset + data.calls.items.length >= data.calls.total} onClick={() => setOffset(offset + data.calls.limit)}>
                  Раньше
                </button>
              </div>
            </>
          )}
          <ClientWebhooksView clientId={cid} canDisable={canSwitch} />
        </>
      )}
    </section>
  );
}

/** The client's webhooks (MK42-6388): read-only — endpoints without secrets, the log — and «Выключить точку». */
function ClientWebhooksView({ clientId, canDisable }: { clientId: string; canDisable: boolean }) {
  const q = useClientWebhooks(clientId);
  const paging = useLogPaging();
  const log = useClientWebhookDeliveries(clientId, paging.filter);
  const disable = useDisableClientWebhook();
  const endpoints = q.data?.endpoints ?? [];
  return (
    <div data-testid="client-webhooks">
      <h3 className="mb-2 mt-6 text-sm font-medium">Вебхуки</h3>
      {!q.data ? (
        <QueryPending query={q} compact />
      ) : (
        <>
          <p className="mb-3 text-sm text-muted">Точки добавляет и проверяет владелец кабинета клиента. Студия видит их и журнал, может выключить точку.</p>
          <WebhookEndpoints
            endpoints={endpoints}
            empty="Вебхуков нет."
            onDisable={canDisable ? (endpointId) => disable.mutateAsync({ clientId, endpointId }) : undefined}
            disabling={disable.isPending}
          />
          {endpoints.length > 0 && (
            <>
              <h4 className="mb-2 mt-4 text-sm font-medium">Журнал доставок</h4>
              <WebhookLog page={log.data} filter={paging.filter} onFilter={paging.setFilter} onOlder={() => paging.older(log.data?.nextBefore)} onNewer={paging.newer} endpoints={endpoints} />
            </>
          )}
        </>
      )}
    </div>
  );
}
